import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { AxeAnalytique, CategorieCharge, Charge, ReglementCharge } from '../../api/comptabilite';
import { fournisseurService, ocrService } from '../../api/services';
import { Fournisseur } from '../../types';
import { formatTND } from '../../utils/formatters';

const today = () => new Date().toISOString().slice(0, 10);

const REGLEMENTS: { value: ReglementCharge; label: string }[] = [
  { value: 'CREDIT', label: 'À crédit (fournisseur)' },
  { value: 'ESPECES', label: 'Espèces (caisse)' },
  { value: 'BANQUE', label: 'Banque' },
];

const Charges: React.FC = () => {
  const [categories, setCategories] = useState<CategorieCharge[]>([]);
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [charges, setCharges] = useState<Charge[]>([]);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const [axes, setAxes] = useState<AxeAnalytique[]>([]);
  const [form, setForm] = useState({
    dateCharge: today(), libelle: '', categorieCode: '', fournisseurId: '',
    montantHT: '', tauxTva: '', retenueSource: '', reglement: 'CREDIT' as ReglementCharge, reference: '',
    axeCode: '',
  });

  const refresh = async () => {
    try {
      const [cats, chs] = await Promise.all([
        comptabiliteService.listCategoriesCharge(),
        comptabiliteService.listCharges(),
      ]);
      setCategories(cats);
      setCharges(chs);
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement impossible.');
    }
    try {
      const page = await fournisseurService.getAll({ size: 500 });
      setFournisseurs(page.content);
    } catch { /* fournisseurs facultatifs */ }
    try {
      setAxes(await comptabiliteService.listAxes()); // TF-061 — axes analytiques facultatifs
    } catch { /* analytique facultative */ }
  };

  useEffect(() => { refresh(); }, []);

  const categorie = categories.find((c) => c.code === form.categorieCode);
  const tauxEffectif = form.tauxTva !== '' ? Number(form.tauxTva) : (categorie?.tauxTvaDefaut ?? 0);
  const ht = Number(form.montantHT) || 0;
  const tvaEstimee = Math.round(ht * tauxEffectif) / 100;
  const ttc = ht + tvaEstimee;

  const choisirCategorie = (code: string) => {
    const cat = categories.find((c) => c.code === code);
    setForm({ ...form, categorieCode: code, tauxTva: cat ? String(cat.tauxTvaDefaut) : form.tauxTva });
  };

  // TF-067 — pré-imputation IA depuis le libellé (l'utilisateur valide ensuite).
  const suggererImputation = async () => {
    if (!form.libelle.trim()) { toast.error('Saisissez d’abord un libellé.'); return; }
    setSuggesting(true);
    try {
      const s = await comptabiliteService.suggererImputation(form.libelle.trim());
      if (s.categorieCode) {
        setForm((prev) => ({
          ...prev,
          categorieCode: s.categorieCode as string,
          tauxTva: s.tauxTvaDefaut != null ? String(s.tauxTvaDefaut) : prev.tauxTva,
        }));
        toast.success(`${s.categorieLibelle} (${s.compteCharge}) — ${s.commentaire}`);
      } else {
        toast(s.commentaire);
      }
    } catch {
      toast.error('Suggestion indisponible.');
    } finally {
      setSuggesting(false);
    }
  };

  const scanner = async (file?: File | null) => {
    if (!file) return;
    setScanning(true);
    try {
      const res = await ocrService.extract(file);
      const f = res.fields || {};
      const ht = f.totalHT ?? (f.totalTTC != null && f.totalTVA != null ? f.totalTTC - f.totalTVA : undefined);
      const taux = f.totalHT && f.totalTVA ? Math.round((f.totalTVA / f.totalHT) * 100) : undefined;
      let fid = form.fournisseurId;
      if (f.supplier) {
        const s = f.supplier.toLowerCase();
        const match = fournisseurs.find((x) => {
          const r = (x.raisonSociale || '').toLowerCase();
          return r && (r.includes(s) || s.includes(r));
        });
        if (match) fid = String(match.id);
      }
      setForm((prev) => ({
        ...prev,
        libelle: prev.libelle || f.supplier || (f.number ? `Facture ${f.number}` : prev.libelle),
        montantHT: ht != null ? String(ht) : prev.montantHT,
        tauxTva: taux != null ? String(taux) : prev.tauxTva,
        reference: f.number || prev.reference,
        dateCharge: f.date || prev.dateCharge,
        fournisseurId: fid,
      }));
      const via = res.usedFallback ? ` (repli ${res.provider})` : ` (${res.provider})`;
      if (!f.supplier && ht == null) toast(`Aucun champ détecté${via}. Complétez manuellement.`);
      else toast.success(`Champs pré-remplis depuis le document${via}.`);
    } catch (e: any) {
      if (e?.response?.status === 402) toast.error("OCR non inclus dans votre offre.");
      else toast.error(e?.response?.data?.message || "Échec de l'analyse OCR.");
    } finally {
      setScanning(false);
    }
  };

  const enregistrer = async () => {
    if (!form.libelle || !form.montantHT) { toast.error('Libellé et montant HT requis.'); return; }
    setBusy(true);
    try {
      const charge = await comptabiliteService.saisirCharge({
        dateCharge: form.dateCharge,
        libelle: form.libelle,
        categorieCode: form.categorieCode || undefined,
        fournisseurId: form.fournisseurId ? Number(form.fournisseurId) : undefined,
        montantHT: Number(form.montantHT),
        tauxTva: form.tauxTva !== '' ? Number(form.tauxTva) : undefined,
        retenueSource: form.retenueSource ? Number(form.retenueSource) : undefined,
        reglement: form.reglement,
        reference: form.reference || undefined,
        axeCode: form.axeCode || undefined,
      });
      toast.success(`Charge enregistrée (écriture ${charge.ecritureNumero || ''}).`);
      setForm({ ...form, libelle: '', montantHT: '', retenueSource: '', reference: '' });
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Enregistrement impossible.');
    } finally { setBusy(false); }
  };

  const supprimer = async (c: Charge) => {
    if (!window.confirm(`Supprimer « ${c.libelle} » et son écriture ?`)) return;
    try {
      await comptabiliteService.deleteCharge(c.id);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/comptabilite" className="text-sm text-blue-600 hover:underline">← Comptabilité</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Charges &amp; dépenses</h1>
        <p className="text-sm text-gray-500">
          Saisissez loyers, notes de frais, honoraires, énergie… Chaque charge génère son écriture comptable.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Nouvelle charge</h2>
          <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold cursor-pointer
                             ${scanning ? 'opacity-60 pointer-events-none' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}>
            {scanning ? 'Analyse OCR…' : '📷 Scanner un reçu / une facture'}
            <input type="file" accept="image/*,application/pdf" className="hidden"
                   onChange={(e) => { scanner(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Catégorie</label>
            <select value={form.categorieCode} onChange={(e) => choisirCategorie(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2">
              <option value="">— Compte par catégorie —</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>{c.libelle} ({c.compteCharge})</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Libellé</label>
            <div className="flex gap-2">
              <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                     className="w-full rounded-lg border border-gray-200 px-3 py-2" placeholder="Loyer bureau — mars 2026" />
              <button type="button" onClick={suggererImputation} disabled={suggesting}
                      className="whitespace-nowrap rounded-lg border border-blue-600 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50">
                {suggesting ? '…' : '✨ Imputer'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input type="date" value={form.dateCharge} onChange={(e) => setForm({ ...form, dateCharge: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Montant HT</label>
            <input type="number" value={form.montantHT} onChange={(e) => setForm({ ...form, montantHT: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">TVA (%)</label>
            <input type="number" value={form.tauxTva} onChange={(e) => setForm({ ...form, tauxTva: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2"
                   placeholder={categorie ? String(categorie.tauxTvaDefaut) : '0'} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Règlement</label>
            <select value={form.reglement} onChange={(e) => setForm({ ...form, reglement: e.target.value as ReglementCharge })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2">
              {REGLEMENTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fournisseur (option)</label>
            <select value={form.fournisseurId} onChange={(e) => setForm({ ...form, fournisseurId: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2">
              <option value="">—</option>
              {fournisseurs.map((f) => <option key={f.id} value={f.id}>{f.raisonSociale}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Retenue à la source (option)</label>
            <input type="number" value={form.retenueSource} onChange={(e) => setForm({ ...form, retenueSource: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Référence (option)</label>
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   className="w-full rounded-lg border border-gray-200 px-3 py-2" placeholder="N° facture fournisseur" />
          </div>
          {axes.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">Axe analytique (option)</label>
              <select value={form.axeCode} onChange={(e) => setForm({ ...form, axeCode: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2">
                <option value="">— Non ventilé —</option>
                {axes.filter((a) => a.actif).map((a) => (
                  <option key={a.id} value={a.code}>{a.code} — {a.libelle}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-100">
          <div className="text-sm text-gray-600">
            TVA estimée <span className="font-semibold">{formatTND(tvaEstimee)}</span>
            <span className="mx-2 text-gray-300">·</span>
            TTC <span className="font-semibold">{formatTND(ttc)}</span>
          </div>
          <button onClick={enregistrer} disabled={busy || !form.libelle || !form.montantHT}
                  className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            {busy ? 'Enregistrement…' : 'Enregistrer la charge'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Libellé</th>
              <th className="text-left px-4 py-3">Compte</th>
              <th className="text-left px-4 py-3">Fournisseur</th>
              <th className="text-right px-4 py-3">HT</th>
              <th className="text-right px-4 py-3">TVA</th>
              <th className="text-right px-4 py-3">TTC</th>
              <th className="text-left px-4 py-3">Écriture</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {charges.map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-4 py-3 text-gray-600">{c.dateCharge}</td>
                <td className="px-4 py-3 text-gray-900">{c.libelle}</td>
                <td className="px-4 py-3 text-gray-600">{c.compteCharge}</td>
                <td className="px-4 py-3 text-gray-600">{c.fournisseurNom || '—'}</td>
                <td className="px-4 py-3 text-right">{formatTND(c.montantHT)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{formatTND(c.montantTva)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatTND(c.montantTTC)}</td>
                <td className="px-4 py-3 text-gray-500">{c.ecritureNumero || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => supprimer(c)} className="text-red-600 hover:underline">Suppr.</button>
                </td>
              </tr>
            ))}
            {charges.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Aucune charge saisie.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Charges;
